import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { dataExportsAPI } from '../../../api';
import { MoonLoader } from 'react-spinners';
import CustomModal from "../CustomModal/CustomModal"; // Import the modal component
import FieldEditor from './CloudFieldEditor';
import MultiSelectDropdown from '../BusinessSelector/MultipleChoicesDropdown';
import FrequencySelector from './CloudFrequencySelector';
import { getUserSession } from '../../../utils/auth';
import { encryptData, decryptData } from '../../../utils/encryption';
import { useNavigate } from 'react-router-dom';
import DatabaseValidationSummary from './TableVerificationSummary';


const SettingForm = ({scheduledSetting=null, setEditingScheduledSetting=null, setRefresh, setShowSettingForm}) => {
    const navigate = useNavigate();
    // If the user is not logged in, redirect to the login page
    useEffect(() => {
        if (!localStorage.getItem('email')) window.location.href = '/auth/signin';
    }, []);

    const fetchUserResponse = useSelector(state => state.fetchUserResponse);

    // Get role and business roles
    const role = localStorage.getItem('rbac_role');
    let businessRoles = [];
    try {
      businessRoles = JSON.parse(localStorage.getItem('rbac_data')) || [];
    } catch {
      businessRoles = [];
    }

    const businesses = fetchUserResponse?.businesses;
    let businessNames = businesses?.map(business => Object.keys(business)[0]);

          // For non-TLA users, filter based on businesses where they have non-technician roles
    if (role?.toLowerCase() !== 'tla') {
      // For non-TLA users, filter based on businesses where they have non-technician roles
      const allowedBusinesses = businessRoles
        .filter(br => br.role?.toLowerCase() !== 'technician') // Exclude technician roles
        .map(br => br.business);
      
      businessNames = businessNames.filter(name => allowedBusinesses.includes(name));
    }
    businessNames = businessNames?.sort();
    
    
    const initBusinessName = scheduledSetting ? scheduledSetting.business_name : (businessNames && businessNames.length > 0 ? businessNames[0] : '');
    const initSelectedSites = scheduledSetting && scheduledSetting?.sites?.map(site => ({site: site.site_name, hwid: site.hwid})) || [];
    const initCustomSelectedTables = scheduledSetting && scheduledSetting?.table_configs || [];
    const initFrequency = scheduledSetting && scheduledSetting?.frequency ? scheduledSetting?.frequency : "";
    
    const [selectedBusiness, setSelectedBusiness] = useState(initBusinessName);
    const [selectedSites, setSelectedSites] = useState(initSelectedSites);
    const [loadingTables, setLoadingTables] = useState(false);
    const [tableInfo, setTableInfo] = useState([]);

    useEffect(() => {
      // get init selected tables, if scheduledSetting is not null, 
      // it is equal to the tables which is saved in scheduledSetting in table_info
      // if scheduledSetting is null, it is equal to []
      if (!tableInfo || tableInfo.length === 0) return;
      if (!scheduledSetting) return;
      const initSelectedTables = tableInfo.filter((table)=>{return (scheduledSetting?.table_configs?.map(table => table.name) || []).includes(table.name)})
      
      setSelectedTables(initSelectedTables);

    }, [tableInfo]);
    
    const filteredSites = businesses?.filter(obj => obj[selectedBusiness] !== undefined);

    useEffect(() => {
        if (businessNames && !selectedBusiness && !scheduledSetting) {
          setSelectedBusiness(businessNames[0]);
        }
    }, [businessNames]);

    const extractAndSortBySite = (filteredSites) => {
        return filteredSites
          .flatMap(item => {
            const key = Object.keys(item)[0]; // Get the key, e.g., "HIVE"
            const siteDataArray = item[key]; // Get all site data from that key (as an array)
            return siteDataArray.map(siteData => ({
                site: siteData.site,
                hwid: siteData.hwid
            }));
        }).sort((a, b) => a.site.localeCompare(b.site)); // Sort by the site property
    };


    // Use useMemo to memorize the sorted sites
    const sortedSites = useMemo(() => {
        if (filteredSites) {
            return extractAndSortBySite(filteredSites);
        }
        return []; // Return an empty array if no filteredSites
    }, [selectedBusiness]); // Recompute when filteredSites changes
    

    const handleBusinessChange = (event) => {
        setSelectedBusiness(event.target.value);
        handleSiteSelectionChange([]);
        setSelectedSites([]);
        
        setSelectedTables([]);
        setCustomSelectedTables([]);

        setSelectAll(false);
        setTableInfo([]);
      };


      
      const getTables = async (business_name) => {
        setLoadingTables(true);
        try {
            let res = await dataExportsAPI.post('/tables', {business_name, selectedSites});

            if (res.status === 200 || res.status === 500) {
                let data = res.data.data;
                data = data.map(site=> ({...site, tables: site.tables.map(table=>({...table, columns: table.columns.map(column=>({...column, columnName: column.field}))}))}))

                if (selectedSites.length > 1) {
                    // Create a Set to hold unique table names
                    let uniqueTableNames = new Set();
                    // Create an array to store unique tables
                    let uniqueTables = [];

                    // Iterate over the list of objects
                    data.forEach(obj => {
                        obj.tables.forEach(table => {
                            if (!uniqueTableNames.has(table.name)) {
                            uniqueTableNames.add(table.name); // Add table name to the Set
                            uniqueTables.push(table); // Add the table to the result array
                        }});
                    });

                    setTableInfo(uniqueTables);

                } else {
                    setTableInfo(data[0]?.tables || []);
                }

            } else {
                toast.error("Failed to get tables");
            }

        } catch (error) {
            // console.log("error", error);
        }
        setLoadingTables(false);

    }
    
    useMemo(() => {
        if (selectedSites.length > 0 && selectedBusiness !== '') {
            getTables(selectedBusiness, selectedSites);
        }
    }, 
    [selectedSites, selectedBusiness]);


    const [selectedTables, setSelectedTables] = useState([]);
  
    // State to manage the "Select All" checkbox
    const [selectAll, setSelectAll] = useState(false);
  
    // Handle individual checkbox selection
    const handleCheckboxChange = (tableObj) => {
      setSelectedTables((prevSelectedTables) => {
        const isSelected = prevSelectedTables.map(t=>t.name).includes(tableObj.name);

        const newSelectedTables = isSelected
          ? prevSelectedTables.filter((t) => t.name !== tableObj.name) // Deselect
          : [...prevSelectedTables, tableObj]; // Select
  
        // Check if all tables are selected
        setSelectAll(newSelectedTables.length === tableInfo.length);
        return newSelectedTables;
      });

      setCustomSelectedTables((prevSelectedTables) => {
        const isSelected = prevSelectedTables.map(t=>t.name).includes(tableObj.name);

        const newSelectedTables = isSelected
          ? prevSelectedTables.filter((t) => t.name !== tableObj.name) // Deselect
          : [...prevSelectedTables, tableObj]; // Select
  
        // Check if all tables are selected
        setSelectAll(newSelectedTables.length === tableInfo.length);
        return newSelectedTables;
      });
    };
  
    // Handle "Select All" checkbox change
    const handleSelectAllChange = () => {
      if (selectAll) {
        setSelectedTables([]); // Deselect all
        setCustomSelectedTables([]);
      } else {
        setSelectedTables(tableInfo.map((t) => t)); // Select all
        // Keep the tables in customSelectedTables, add new tables to it
        setCustomSelectedTables((prevSelectedTables) => {
          // Get the names of the tables already in customSelectedTables
          const prevTableNames = prevSelectedTables.map(t => t.name);
          // Get the names of the tables that are not already in customSelectedTables
          const newTables = tableInfo.filter(t => !prevTableNames.includes(t.name));
          // Return the union of the two sets
          return [...prevSelectedTables, ...newTables];
        });
      }
      setSelectAll(!selectAll); // Toggle the "Select All" state
    };

    const selectedCount = selectedTables.length;


    const [showModal, setShowModal] = useState(false);
    const [tablePoppedUp, setTablePoppedUp] = useState({}); //advanced table info
    const [selectedColumnObjs, setSelectedColumnObjs] = useState([]);
    const [customTableMetaSetting, setCustomTableMetaSetting] = useState({"targetName": "", "createIfNonExist": false});
    const [customSelectedTables, setCustomSelectedTables] = useState(initCustomSelectedTables);


    const handleSiteSelectionChange = (selected) => {
      setSelectedSites(selected);
    };

    const isTableChecked = (tableObj) => {
      return (selectedTables && selectedTables.length !== 0) ? selectedTables.map((t)=>t.name).includes(tableObj.name) : false
    }

    const handleOpenAdvanced = (tableObj) => {
      setTablePoppedUp(tableObj); 
      // find table in customSelectedTables with the same name as tableObj
      let table = customSelectedTables.find(t => t.name === tableObj.name);
      if (table) {
        setSelectedColumnObjs(table.columns)
        if (!table.targetName || table.targetName === "") {
          setCustomTableMetaSetting({targetName: tableObj.name, createIfNonExist: table.createIfNonExist});
        } else {
          setCustomTableMetaSetting({targetName: table.targetName, createIfNonExist: table.createIfNonExist});
        }
      
      } else {
        setSelectedColumnObjs(tableObj.columns);
        setCustomTableMetaSetting({"targetName":tableObj.name, "createIfNonExist": false});

      }
      setShowModal(true)
    }

    const [frequency, setFrequency] = useState(initFrequency); // Daily or Weekly as a string
    
    let emptyCloudDatabaseUserInput = {
      "dbName": "",
      "username": "",
      "password": "",
      "host": "",
      "port": "3306",
      "provider": ""
    }
    const [cloudDatabaseUserInput, setCloudDatabaseUserInput] = useState(emptyCloudDatabaseUserInput);

    useEffect(() => {
      const fetchDecryptedData = async () => {
        if (scheduledSetting && scheduledSetting.cloud_setting_obj) {
          let decryptedData = await decryptData(
            scheduledSetting.cloud_setting_obj.encryptedCredentials,
            scheduledSetting.cloud_setting_obj.iv
          );
          setCloudDatabaseUserInput(decryptedData);
        }
      };
    
      fetchDecryptedData();
    }, []);
    


    const saveSetting = async () => {
      // // console.log("saveSetting");
      // logger.info("saveSetting");
      // logger.error("saveSetting", {"msg": "error"});

      
      try{
        let idToken = await getUserSession();

        if (!idToken) {
          toast.error('No authentication token found. Please sign in again.');
          setTimeout(() => {
            navigate('/auth/signin');
          }, 1000); // Delay navigation for 3 seconds (3000ms)
          return;
        }
  
        let credentials = cloudDatabaseUserInput;
        let { encryptedBase64, ivBase64 } = await encryptData(credentials);

        let mysqlConnRes = await dataExportsAPI.post('/cloud-delivery-settings/test-mysql-connection', 
          { 
            encryptedCredentials: encryptedBase64,
            iv: ivBase64,
          },);
        
          
        if (mysqlConnRes.status !== 200) {
          toast.error("Failed to save setting. MYSQL credentials are incorrect.");
          // toast.error("Failed to save setting. MYSQL credentials are incorrect. Details: " + mysqlConnRes.data.message);
          return;
        }

        // Verify the access

        let verifyAccessRes = await dataExportsAPI.post('/cloud-delivery-settings/verify-access',
          { 
            encryptedCredentials: encryptedBase64,
            iv: ivBase64,
            tableConfigurations: customSelectedTables,
          },);

        if (verifyAccessRes.data.results.failure.length > 0) {
          setTableVerificationModalShows(true);
          setTableVerificationModalData(verifyAccessRes.data.results);
          return;
        }


        let hw_ids = selectedSites.map(site => site.hwid);
        let formatted_customSelectedTables = customSelectedTables.map(table => ({
          name: table.name,
          columns: table.columns,
          targetName: table.targetName,
          createIfNonExist: table.createIfNonExist,
        }));


        let data_passed ={
          encryptedCredentials: encryptedBase64,
          iv: ivBase64,
          business_name: selectedBusiness,
          user_email: localStorage.getItem('email'),
          hw_ids: hw_ids,
          table_configs: formatted_customSelectedTables,
          frequency: frequency,
        }
       
        const response = await dataExportsAPI.post("/cloud-delivery-settings", data_passed,           
        {
          headers: {
            'Authorization': idToken
          }
        });
        
        if (response.status === 201) {
          toast.success("Setting saved successfully. Refreshing page...");
          setRefresh(new Date().getTime());
          setShowSettingForm(false);

        } else {
          toast.error("Failed to save setting. Details: " + response.data.message);
        }

      } catch (error) {
        // console.log("miao~ error", error);
        toast.error(`Failed to save setting. ${error?.response?.data?.error?.message || error?.response?.data?.error || ""}` );
      }
    
    }


    const [tableVerificationModalShows, setTableVerificationModalShows] = useState(false);
    const [tableVerificationModalData, setTableVerificationModalData] = useState({});

    const updateSetting = async () => {
      try{

        let idToken = await getUserSession();

        if (!idToken) {
          toast.error('No authentication token found. Please sign in again.');
          setTimeout(() => {
            navigate('/auth/signin');
          }, 1000); // Delay navigation for 3 seconds (3000ms)
          return;
        }

        let credentials = cloudDatabaseUserInput;
        let { encryptedBase64, ivBase64 } = await encryptData(credentials);

        // Test connection

        let mysqlConnRes = await dataExportsAPI.post('/cloud-delivery-settings/test-mysql-connection', 
          { 
            encryptedCredentials: encryptedBase64,
            iv: ivBase64,
          },);

        if (mysqlConnRes.status !== 200) {
          toast.error("Failed to save setting. MYSQL credentials are incorrect. Details: " + mysqlConnRes.data.message);
          return;
        }


        // Verify the access
        
        let verifyAccessRes = await dataExportsAPI.post('/cloud-delivery-settings/verify-access',
          { 
            encryptedCredentials: encryptedBase64,
            iv: ivBase64,
            tableConfigurations: customSelectedTables,
          },);

        if (verifyAccessRes.data.results.failure.length > 0) {
          setTableVerificationModalShows(true);
          setTableVerificationModalData(verifyAccessRes.data.results);
          return;
        }

        let hw_ids = selectedSites.map(site => site.hwid);
        let formatted_customSelectedTables = customSelectedTables.map(table => ({
          name: table.name,
          columns: table.columns,
          targetName: table.targetName,
          createIfNonExist: table.createIfNonExist,
        }));


        let data_passed ={
          business_name: selectedBusiness,
          encryptedCredentials: encryptedBase64,
          iv: ivBase64,
          user_email: localStorage.getItem('email'),
          cloud_credential_id: scheduledSetting.cloud_credential_id,
          hw_ids: hw_ids,
          table_configs: formatted_customSelectedTables,
          frequency: frequency,
        }
        // console.log("data_passed === ", data_passed);
        const response = await dataExportsAPI.put(`/cloud-delivery-settings/${scheduledSetting.id}`, 
          data_passed, 
          {
          headers: {
            'Authorization': idToken
          }
        });
        
        if (response.status === 200) {
          toast.success("Setting updated successfully. Refreshing page...");
          setEditingScheduledSetting({});
          setRefresh(new Date().getTime());

        } else {
          // console.log("response", response);
          toast.error(`Failed to update setting. ${response.data.message || ""}` );
        }

      } catch (error) {
        // console.log("error", error);
        toast.error(`Failed to update setting. ${error?.response?.data?.error?.message || error?.response?.data?.error || ""}` );
      }
    }


    const dropChanges = () => {
      setEditingScheduledSetting({});
      setShowSettingForm(false);
    }


    const deleteSetting = async () => {
      try{

        let idToken = await getUserSession();

        if (!idToken) {
          toast.error('No authentication token found. Please sign in again.');
          setTimeout(() => {
            navigate('/auth/signin');
          }, 1000); // Delay navigation for 3 seconds (3000ms)
          return;
        }

        const response = await dataExportsAPI.delete(`/cloud-delivery-settings/${scheduledSetting.id}`,
          {
          headers: {
            'Authorization': idToken
          }
        });
        
        if (response.status === 200) {
          toast.success("Setting deleted successfully. Refreshing page...");
          setEditingScheduledSetting({});
          setRefresh(new Date().getTime());

        } else {
          toast.error("Failed to delete setting");
        }

      } catch (error) {
        // console.log("error", error);
        toast.error("Failed to delete setting");
      }
    }

    return (
        <div className='setting-form-container'>
          <div className='setting-form-header'>
            <CustomModal show={tableVerificationModalShows} 
              handleClose={() => setTableVerificationModalShows(false)} 
              title="Database Verification Summary">
              <DatabaseValidationSummary results={tableVerificationModalData} />
            </CustomModal>
            <h2>
              {scheduledSetting && scheduledSetting.id ? "Edit" : "Create"} Scheduled Delivery for {selectedBusiness}
            </h2>
            {
              scheduledSetting && scheduledSetting.id 
              ? <div className='setting-form-header-actions'>
                <button className='setting-form-btn-cancel' onClick={dropChanges}>Cancel</button>
                <button className='setting-form-btn-delete' onClick={deleteSetting}>Delete</button>
              </div>
              : <div className='setting-form-header-actions'>
                <button className='setting-form-btn-update' onClick={saveSetting}>SAVE</button>
                <button className='setting-form-btn-cancel' onClick={dropChanges}>Cancel</button>
              </div>
            }
          </div>
            <div className='setting-form-config-section'>
              <h3>Cloud Endpoint</h3>
              <CloudEndpoint userInput={cloudDatabaseUserInput} setUserInput={setCloudDatabaseUserInput} />
            </div>
                        
            <div className='setting-form-config-section'>
                <h3>Data Source</h3>
                <div className='setting-form-input-group'>
                    <label >Select a business:</label>
                    <select className='setting-form-dark-select' value={selectedBusiness} style={{width: "100%", padding: "5px 3px"}}
                      onChange={(event)=>{handleBusinessChange(event)}} disabled={loadingTables}>
                        <option value="">Please select..</option>
                    {businessNames?.map((business, index) => (
                        <option key={index} value={business}>{business}</option>
                    ))}
                    </select>
                </div>
                
                <div className='setting-form-input-group'>
                  <label>Select sites:</label>
                  <MultiSelectDropdown 
                    sites={sortedSites} 
                    onChange={handleSiteSelectionChange} 
                    loading={loadingTables} 
                    selectedSites={selectedSites}
                    setSelectedSites={setSelectedSites}
                  />
                </div>
                
                <div >
                    <MoonLoader loading={loadingTables} size={16} color={"white"} />
                </div>
            </div>
            
            <CustomModal
              show={showModal}
              handleClose={() => {
                // console.log("handleClose", customTableMetaSetting);
                setShowModal(false); 
                setCustomSelectedTables(customSelectedTables.map(table => table.name === tablePoppedUp.name ? { ...table, columns: selectedColumnObjs, ...customTableMetaSetting } : table));
                setSelectedColumnObjs([]);
                setCustomTableMetaSetting({"targetName": "", "createIfNonExist": false});
              }}
              title={tablePoppedUp.name}
            >
              {
                <FieldEditor tablePoppedUp={tablePoppedUp} 
                selectedColumnObjs={selectedColumnObjs} setSelectedColumnObjs={setSelectedColumnObjs} 
                customTableMetaSetting={customTableMetaSetting} setCustomTableMetaSetting={setCustomTableMetaSetting}
                />
              }

            </CustomModal>
            
            <div className='setting-form-config-section'>
              <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <h3>Select Tables</h3>
              </div>
              <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                  <div style={{marginBottom: "10px"}}>
                    <input
                        type="checkbox"
                        id="selectAll"
                        name="selectAll"
                        value="selectAll"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        disabled={!tableInfo || tableInfo.length === 0}
                    />
                      <label htmlFor="selectAll"> Select All</label>
                  </div>
                  <div className='setting-form-table-count'>
                    <div style={{marginRight:"5px"}}>{selectedCount} tables selected</div>
                  </div>
              </div>

              <div>
                  {
                      loadingTables ? (
                          <div>Loading tables...</div>
                      ) : tableInfo?.length === 0 ? (
                          <div>No tables found</div>
                      ) : (
                          <div className='setting-form-tables-grid'>
                            {tableInfo?.map((tableObj, index) => (
                                <div className='setting-form-table-item' key={index}>
                                  <div style={{display: 'flex', alignItems: 'center'}}>
                                    <input
                                      type="checkbox"
                                      id={tableObj.name}
                                      name={tableObj.name}
                                      value={tableObj.name}
                                      checked={isTableChecked(tableObj)}
                                      onChange={() => handleCheckboxChange(tableObj)}
                                    />
                                    <label htmlFor={tableObj.name}>{tableObj.name}</label>
                                  </div>
                                  {
                                    // isTableChecked(tableObj) &&
                                    <span className='setting-form-table-tag'
                                    onClick={()=>{handleOpenAdvanced(tableObj)}}>Advanced</span>
                                  }
                                </div>
                            ))}
                          </div>
                      )
                  }
              </div>
            </div>
            
            <FrequencySelector frequency={frequency} setFrequency={setFrequency} 
              customStyles={{width:"600px", borderRadius: "10px", padding: "20px", marginBottom: "10px", }} />

            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
              {
                scheduledSetting && scheduledSetting.id 
                && <div className='setting-form-header-actions'>
                <button className='setting-form-btn-update' onClick={updateSetting} >Verify and Update</button>
              </div>
              }
            </div>
        </div>
    )
}


export default SettingForm;





// Help me write a components
// first, list users to select aws rds mysql or google cloud mysql (please use a radio button)
// once user select one of them, please list 4 fields to fill in. If user didn't select any of them, please show a message to select one of them
// 1. database name
// 2. username and 3. password
// 4. host and 5. port
// A test connection button should be there to test the connection
// If the connection is successful, a success message should be displayed
// If the connection is not successful, an error message should be displayed
// No need to add className, I will follow the design of the other components
const CloudEndpoint = ({userInput, setUserInput}) => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);

    const [status, setStatus] = useState('');

    const handleCloudChange = (event) => {
      setUserInput({...userInput, provider: event.target.value});
    };

    const testConnection = async () => {
        setLoading(true);
        try {
          let credentials = userInput;
          let { encryptedBase64, ivBase64 } = await encryptData(credentials);

            let res = await dataExportsAPI.post('/cloud-delivery-settings/test-mysql-connection', 
              { 
                encryptedCredentials: encryptedBase64,
                iv: ivBase64,
              },);

            if (res.status === 200) {
                toast.success("Connection successful");
                setSuccess(true);
                setError(false);
            } else {
              toast.error(`${res.data?.message} Details: ${res.data.error?.message}` || "Connection failed");
                setSuccess(false);
                setError(true);
            }
        } catch (error) {
          // console.log("error", error);
          toast.error(`${error.response?.data?.message}. Details: ${error.response?.data?.error?.message}` || "Connection failed");
            setSuccess(false);
            setError(true);
        }
        setLoading(false);
    };


    return (
        <div className='setting-form-input-group'>
            <div>
              <label>Select a cloud provider:</label>
              <div className="setting-form-frequency-buttons">
                <button 
                  className={`setting-form-freq-btn ${userInput.provider === "aws-rds" ? 'active' : ''}`}
                  onClick={() => {
                    if (userInput.provider !== "aws-rds") {
                      setUserInput({
                        ...userInput, 
                        provider: "aws-rds",
                        host: "",
                      });
                    }
                  }}>
                  AWS RDS MySQL
                </button>
                <button 
                  className={`setting-form-freq-btn ${userInput.provider === "gcp-cloud-sql" ? 'active' : ''}`}
                  onClick={() => {
                    if (userInput.provider !== "gcp-cloud-sql") {
                      setUserInput({
                        ...userInput, 
                        provider: "gcp-cloud-sql",
                        host: "",
                      });
                    }
                  }}>
                  Google Cloud MySQL
                </button>
              </div>
              <div className="setting-form-selected-frequencies">Selected: {userInput.provider || "None"}</div>    
            </div>

            {
                userInput.provider!== '' &&
                <div style={{marginTop: "15px"}}>
                  <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <div className='setting-form-input-group' style={{flex: 1}}>
                        <label>{userInput.provider === "gcp-cloud-sql" ? "Public IP" : "Host"}</label>
                        <input type="text" value={userInput.host} onChange={(e)=> setUserInput({...userInput, "host": e.target.value})} />
                    </div>
                    <div className='setting-form-input-group' style={{flex: 1, marginLeft: "10px"}}>
                        <label>Database Name:</label>
                        <input type="text" value={userInput.dbName} onChange={(e)=> setUserInput({...userInput, "dbName": e.target.value})} />
                    </div>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <div className='setting-form-input-group' style={{flex: 1}}>
                        <label>Username:</label>
                        <input type="text" value={userInput.username} onChange={(e)=> setUserInput({...userInput, "username": e.target.value})} />
                    </div>
                    <div className='setting-form-input-group' style={{flex: 1, marginLeft: "10px"}}>
                        <label>Password:</label>
                        <input type="password" value={userInput.password} onChange={(e)=> setUserInput({...userInput, "password": e.target.value})} />
                    </div>
                  </div>
                  <div className='setting-form-header-actions' style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
                    <button className='setting-form-btn-update' onClick={testConnection} disabled={loading}>Test Connection</button>
                  </div>
                </div>
            }
        </div>
    );

}
